import { css } from 'styled-components';

import { buttonTokens } from '../../tokens/components/Button/tokens';
import { anchorTokens } from '../../tokens/components/Anchor/tokens';
import CoreRow from '../NewTable/_common/CoreRow';
import { TooltipContent } from '../Tooltip/styles';

// Normalize buttons NOT part of Button component
export const resetBrowserButton = css`
  appearance: none;
  background-color: inherit;
  border: 0;
  color: inherit;
  font-size: inherit;
  margin: 0;
  padding: 0;
  text-align: unset;

  &:hover {
    text-decoration: none;
    cursor: pointer;
  }

  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }
`;

// Normalizes button that inherits or related to Button component
export const buttonBase = css`
  align-items: center;
  appearance: none;
  background-color: ${buttonTokens.BackgroundColorBaseDefault};
  border-color: ${buttonTokens.BorderColorBaseDefault};
  border-radius: ${buttonTokens.BorderRadiusBaseDefault};
  border-style: ${buttonTokens.BorderStyleBaseDefault};
  border-width: ${buttonTokens.BorderWidthBaseDefault};
  display: inline-flex;
  font-size: ${buttonTokens.FontSizeBaseDefault};
  font-weight: ${buttonTokens.FontWeightBaseDefault};
  justify-content: center;
  margin: 0;
  padding: 0;
  transition-duration: 80ms;
  transition-property: background-color, border-color, box-shadow, color, outline;
  transition-timing-function: ease-out;
  white-space: nowrap;

  &:hover {
    text-decoration: none;
    cursor: pointer;
  }

  // Focus shadow and border should superseed regular hover styles.
  // This uses double ampersand as a specificity boost to override hover styles
  &:focus-visible,
  &&:focus-visible:hover {
    border-color: ${buttonTokens.BorderColorBaseKeyboardFocus};
    box-shadow: 0 0 0 ${buttonTokens.ShadowWidthBaseKeyboardFocus} ${buttonTokens.ShadowColorBaseKeyboardFocus};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }

  // Cannot use :disabled until we deprecate <Button href> as code is shared
  ${({ theme }) =>
    theme.disabled &&
    css`
      &:focus-visible {
        outline: none;
      }
    `};
`;

export const buttonVariants = {
  actions: css`
    background-color: ${buttonTokens.BackgroundColorActionsDefault};
    border: 1px solid;
    border-color: ${buttonTokens.BorderColorActionsDefault};
    border-radius: 50%;
    padding: 0;
    min-width: ${buttonTokens.SizeWidthActionsDefault};
    min-height: ${buttonTokens.SizeHeightActionsDefault};
    color: ${buttonTokens.FontColorActionsDefault};

    &:hover {
      border-color: ${buttonTokens.BorderColorActionsHover};
    }

    &:active {
      background-color: ${buttonTokens.BackgroundColorActionsDown};
      border-color: ${buttonTokens.BorderColorActionsDown};
    }

    ${({ theme }) =>
      theme.active &&
      css`
        &,
        &:hover,
        &:active {
          background-color: ${buttonTokens.BackgroundColorActionsDown};
          border-color: ${buttonTokens.BorderColorActionsDown};
        }
      `};

    // Background appears on table row hover (not just directly hovering)
    ${CoreRow}:hover && {
      // TODO: check that actions hover on row styles is captured in backstop
      background-color: ${buttonTokens.BackgroundColorActionsInTableRowHover};

      // But uses a transparency for colored rows
      ${({ theme }) =>
        theme.bgColor !== 'white' &&
        css`
          // TODO: check that actions hover on row styles is captured in backstop
          background-color: ${buttonTokens.BackgroundColorActionsInTableColoredRowHover};
        `}

      ${({ theme }) =>
        (theme.rowVariant || theme.cellVariant) &&
        css`
          background-color: ${buttonTokens.BackgroundColorActionsInTableColoredRowHover};
        `}
    }
  `,
  aura: css`
    background: none;
    border: none;
    padding: 0;
    color: inherit;

    // Cannot use :disabled until we deprecate <Button href> as code is shared
    ${({ theme }) =>
      theme.disabled &&
      css`
        &&,
        &:hover,
        &:active {
          opacity: ${buttonTokens.OpacityAuraDisabled};
        }
      `};

    // <Search> uses some deeply nested legacy styles...
    .ink-control__disabled .wrapper-dropdown & {
      opacity: 1;
    }
  `,
  danger: css`
    background-color: ${buttonTokens.BackgroundColorDangerDefault};
    border-color: ${buttonTokens.BorderColorDangerDefault};
    color: ${buttonTokens.FontColorDangerDefault};

    &:hover:not(:disabled) {
      background-color: ${buttonTokens.BackgroundColorDangerHover};
      border-color: ${buttonTokens.BorderColorDangerHover};
    }

    &:active:not(:disabled) {
      background-color: ${buttonTokens.BackgroundColorDangerDown};
    }

    // Custom focus visuals for danger (similar to invalid input)
    &:focus-visible,
    &&:focus-visible:hover {
      border-color: ${buttonTokens.BorderColorDangerKeyboardFocus};
      box-shadow: 0 0 0 ${buttonTokens.ShadowWidthBaseKeyboardFocus} ${buttonTokens.ShadowColorDangerKeyboardFocus};
    }

    ${({ theme }) =>
      theme.active &&
      css`
        &&,
        &:hover:not(:disabled),
        &:active:not(:disabled) {
          background-color: ${buttonTokens.BackgroundColorDangerDown};
        }
      `};

    // Cannot use :disabled until we deprecate <Button href> as code is shared
    ${({ theme }) =>
      theme.disabled &&
      css`
        background-color: ${buttonTokens.BackgroundColorDangerDisabled};
        color: ${buttonTokens.FontColorDangerDisabled};
      `};
  `,
  // to be renamed as secondary
  default: css`
    background-color: ${buttonTokens.BackgroundColorSecondaryDefault};
    border-color: ${buttonTokens.BorderColorSecondaryDefault};
    color: ${buttonTokens.FontColorSecondaryDefault};

    &:hover:not(:disabled) {
      border-color: ${buttonTokens.BorderColorSecondaryHover};
      color: ${buttonTokens.FontColorSecondaryHover};
    }

    &:active:not(:disabled) {
      border-color: ${buttonTokens.BorderColorSecondaryDown};
      color: ${buttonTokens.FontColorSecondaryDown};
    }

    ${({ theme }) =>
      theme.active &&
      css`
        &&,
        &:hover:not(:disabled),
        &:active:not(:disabled) {
          border-color: ${buttonTokens.BorderColorSecondaryDown};
          color: ${buttonTokens.FontColorSecondaryDown};
        }
      `};

    // Cannot use :disabled until we deprecate <Button href> as code is shared
    ${({ theme }) =>
      theme.disabled &&
      css`
        border-color: ${buttonTokens.BorderColorSecondaryDefault};
        background-color: ${buttonTokens.BackgroundColorSecondaryDisabled};
        color: ${buttonTokens.FontColorSecondaryDisabled};
      `};
  `,
  filter: css`
    background-color: ${buttonTokens.BackgroundColorFilterDefault};
    border-color: ${buttonTokens.BorderColorFilterDefault};
    border-radius: ${buttonTokens.BorderRadiusFilterDefault};
    padding: 0 ${buttonTokens.SpacingHorizontalFilterDefault};
    color: ${buttonTokens.FontColorFilterDefault};
    &:hover:not(:disabled) {
      border-color: ${buttonTokens.BorderColorFilterHover};
      color: ${buttonTokens.FontColorFilterHover};
    }
    &:active:not(:disabled) {
      border-color: ${buttonTokens.BorderColorFilterDown};
      color: ${buttonTokens.FontColorFilterDown};
      background-color: ${buttonTokens.BackgroundColorFilterDown};
    }
    ${({ theme }) =>
      theme.active &&
      css`
        /** Unsure if we should tokenize a sub-pixel value */
        border-width: 1.5px;
        border-color: ${buttonTokens.BorderColorFilterActive};
        color: ${buttonTokens.FontColorFilterActive};
        padding: 0 ${buttonTokens.SpacingHorizontalFilterActive};
      `};
    // Cannot use :disabled until we deprecate <Button href> as code is shared
    ${({ theme }) =>
      theme.disabled &&
      css`
        border-color: ${buttonTokens.BorderColorFilterDefault};
        background-color: ${buttonTokens.BackgroundColorFilterDisabled};
        color: ${buttonTokens.FontColorFilterDisabled};
      `};
  `,
  inverse: css`
    background-color: ${buttonTokens.BackgroundColorInverseDefault};
    color: ${buttonTokens.FontColorInverseDefault};

    &:hover:not(:disabled) {
      border-color: ${buttonTokens.BorderColorInverseHover};
      color: ${buttonTokens.FontColorInverseHover};
    }

    &:active:not(:disabled) {
      border-color: ${buttonTokens.BorderColorInverseDown};
      color: ${buttonTokens.FontColorInverseDown};
    }

    ${({ theme }) =>
      theme.active &&
      css`
        &&,
        &:hover:not(:disabled),
        &:active:not(:disabled) {
          border-color: ${buttonTokens.BorderColorInverseDown};
        }
      `};
  `,
  link: css`
    background: none;
    border-radius: ${buttonTokens.BorderRadiusLinkDefault};
    border: none;
    color: ${buttonTokens.FontColorLinkDefault};
    font-size: inherit;
    font-weight: ${buttonTokens.FontWeightLinkDefault};
    justify-content: initial;
    min-height: 0;
    padding: 0;
    text-align: left;
    width: auto;
    text-decoration: ${buttonTokens.FontDecorationLinkDefault};
    text-underline-offset: 0.15em;

    // Link variant uses outline instead of border to prevent
    // changes to box size, as it is used inline with text
    // (Double ampersand specificity boost required)
    &:focus-visible,
    &&:focus-visible:hover {
      outline: ${buttonTokens.BorderWidthBaseDefault} ${buttonTokens.BorderStyleBaseDefault}
        ${buttonTokens.BorderColorBaseKeyboardFocus};
    }

    // wrapText is *only* for link
    ${({ theme }) =>
      theme.wrapText &&
      css`
        display: inline;
        white-space: normal;
      `};

    ${TooltipContent} && {
      // lightens the anchor color for better contrast.
      color: ${buttonTokens.FontColorLinkInverse};

      &:active {
        color: ${buttonTokens.FontColorLinkInverse};
      }

      ${({ theme }) =>
        theme.tooltipVariant &&
        theme.tooltipVariant === 'feedback-negative' &&
        css`
          // Uses 'underline' link styles inside negative tooltips (a la Banner)
          color: ${anchorTokens.UnderlineTextColorInitial};

          &:hover {
            color: ${anchorTokens.UnderlineTextColorHover};
          }

          &:active {
            color: ${anchorTokens.UnderlineTextColorActive};
          }
        `}
    }

    &:hover {
      color: ${buttonTokens.FontColorLinkHover};
      text-decoration: ${buttonTokens.FontDecorationLinkDefault};
    }

    &:active {
      color: ${buttonTokens.FontColorLinkDown};
    }

    ${({ theme }) =>
      theme.disabled &&
      css`
        color: ${buttonTokens.FontColorLinkDisabled};
      `}
  `,
  primary: css`
    background-color: ${buttonTokens.BackgroundColorPrimaryDefault};
    border-color: ${buttonTokens.BorderColorPrimaryDefault};
    color: ${buttonTokens.FontColorPrimaryDefault};

    &:hover:not(:disabled) {
      background-color: ${buttonTokens.BackgroundColorPrimaryHover};
      border-color: ${buttonTokens.BorderColorPrimaryHover};
    }

    &:active:not(:disabled) {
      background-color: ${buttonTokens.BackgroundColorPrimaryDown};
    }

    ${({ theme }) =>
      theme.active &&
      css`
        &&,
        &:hover:not(:disabled),
        &:active:not(:disabled) {
          background-color: ${buttonTokens.BackgroundColorPrimaryDown};
        }
      `};

    // Cannot use :disabled until we deprecate <Button href> as code is shared
    ${({ theme }) =>
      theme.disabled &&
      css`
        background-color: ${buttonTokens.BackgroundColorPrimaryDisabled};
        color: ${buttonTokens.FontColorPrimaryDisabled};
      `};
  `,
  transparent: css`
    background-color: ${buttonTokens.BackgroundColorTransparentDefault};
    border-color: ${buttonTokens.BorderColorTransparentDefault};
    color: ${buttonTokens.FontColorTransparentDefault};

    &:hover:not(:disabled) {
      color: ${buttonTokens.FontColorTransparentHover};
    }

    &:active:not(:disabled) {
      color: ${buttonTokens.FontColorTransparentDown};
    }

    ${({ theme }) =>
      theme.active &&
      css`
        &&,
        &:hover:not(:disabled),
        &:active:not(:disabled) {
          color: ${buttonTokens.FontColorTransparentDown};
        }
      `};

    // Cannot use :disabled until we deprecate <Button href> as code is shared
    ${({ theme }) =>
      theme.disabled &&
      css`
        color: ${buttonTokens.FontColorTransparentDisabled};
        opacity: ${buttonTokens.OpacityTransparentDisabled};
      `};
  `,
  plus: css`
    background-color: ${buttonTokens.BackgroundColorPlusDefault};
    border-color: ${buttonTokens.BorderColorPlusDefault};
    border-radius: ${buttonTokens.BorderRadiusPlusDefault};
    color: ${buttonTokens.FontColorPlusDefault};
    min-height: ${buttonTokens.SizeHeightPlusDefault};
    padding-top: 2px;
    padding-bottom: 2px;
    padding-left: ${buttonTokens.SpacingHorizontalPlusDefault};
    padding-right: ${buttonTokens.SpacingHorizontalPlusDefault};

    &:hover:not(:disabled) {
      background-color: ${buttonTokens.BackgroundColorPlusHover};
      border-color: ${buttonTokens.BorderColorPlusHover};
    }

    &:active:not(:disabled) {
      background-color: ${buttonTokens.BackgroundColorPlusDown};
    }
  `,
  minus: css`
    background-color: ${buttonTokens.BackgroundColorMinusDefault};
    border-color: ${buttonTokens.BorderColorMinusDefault};
    border-radius: ${buttonTokens.BorderRadiusMinusDefault};
    color: ${buttonTokens.FontColorMinusDefault};
    min-height: ${buttonTokens.SizeHeightMinusDefault};
    padding-top: 2px;
    padding-bottom: 2px;
    padding-left: ${buttonTokens.SpacingHorizontalMinusDefault};
    padding-right: ${buttonTokens.SpacingHorizontalMinusDefault};

    // only minus has this
    margin-left: 0.25rem;

    &:hover:not(:disabled) {
      background-color: ${buttonTokens.BackgroundColorMinusHover};
      border-color: ${buttonTokens.BorderColorMinusHover};
      color: ${buttonTokens.FontColorMinusHover};
    }

    &:active:not(:disabled) {
      background-color: ${buttonTokens.BackgroundColorMinusDown};
      border-color: ${buttonTokens.BorderColorMinusDown};
      color: ${buttonTokens.FontColorMinusDown};
    }
  `,
};

export const buttonSizes = {
  auto: css`
    min-height: ${buttonTokens.SizeHeightAutoDefault};
    padding-left: ${buttonTokens.SpacingHorizontalAutoDefault};
    padding-right: ${buttonTokens.SpacingHorizontalAutoDefault};
  `,
  cell: css`
    min-height: ${buttonTokens.SizeHeightCellDefault};
    padding-left: ${buttonTokens.SpacingHorizontalCellDefault};
    padding-right: ${buttonTokens.SpacingHorizontalCellDefault};
  `,
  full: css`
    min-height: ${buttonTokens.SizeHeightFullDefault};
    padding: 0;
    width: 100%;
  `,
  medium: css`
    // This is only used in Banner Actions so far
    min-height: ${buttonTokens.SizeHeightMediumDefault};
    padding-right: ${buttonTokens.SpacingHorizontalMediumDefault};
    padding-left: ${buttonTokens.SpacingHorizontalMediumDefault};
  `,
  mini: css`
    // Variant should be deprecated (does not exist in refresh)
    border-radius: ${buttonTokens.BorderRadiusMiniDefault};
    font-size: ${buttonTokens.FontSizeMiniDefault};
    line-height: 1;
    min-height: 0;
    padding-left: ${buttonTokens.SpacingHorizontalMiniDefault};
    padding-right: ${buttonTokens.SpacingHorizontalMiniDefault};
  `,
  small: css`
    border-radius: ${buttonTokens.BorderRadiusBaseSmall};
    font-size: ${buttonTokens.FontSizeSmallDefault};
    min-height: ${buttonTokens.SizeHeightSmallDefault};
    padding-left: ${buttonTokens.SpacingHorizontalSmallDefault};
    padding-right: ${buttonTokens.SpacingHorizontalSmallDefault};
  `,
  wide: css`
    min-height: ${buttonTokens.SizeHeightWideDefault};
    padding-left: ${buttonTokens.SpacingHorizontalWideDefault};
    padding-right: ${buttonTokens.SpacingHorizontalWideDefault};
  `,
};
